import * as React from 'react';
import { orgToday } from '@/lib/today';
import { Plus, Trash2 } from 'lucide-react';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, CardHeader, CardTitle,
  Checkbox, Combobox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  Input, Label, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { ReceiptArt } from '@/components/illustrations';
import { ApiError, toastFailure } from '@/lib/api';
import { useProjects } from '@/lib/catalog-api';
import {
  useDeleteExpense, useExpenseCategories, useExpenses, useSaveExpense,
  useSaveExpenseCategory, type ExpenseRow,
} from '@/lib/expense-api';
import { money } from '@/lib/invoice-api';
import type { Session } from '@/lib/session';
import { atLeast, endOfMonth, startOfMonth } from '@cfl/shared';

/**
 * Expenses: money spent on a project, as opposed to time spent on it.
 *
 * Cost and charge are two fields rather than one because they genuinely differ -
 * a cost the company absorbs is charged at zero, and a handling markup charges
 * more than it cost. Only the charge reaches an invoice.
 */
export function ExpensesPage({ session }: { session: Session }) {
  const [from, setFrom] = React.useState(() => monthStart());
  const [to, setTo] = React.useState(() => monthEnd());
  const { data, isPending } = useExpenses({ from, to });
  const { data: categories } = useExpenseCategories();
  const [editing, setEditing] = React.useState<ExpenseRow | 'new' | null>(null);
  const [managingCategories, setManagingCategories] = React.useState(false);
  const remove = useDeleteExpense();
  const [error, setError] = React.useState<string | null>(null);

  const mayManage = atLeast(session.role, 'HR');
  const noCategories = categories !== undefined && categories.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Expenses</h1>
          <p className="text-sm text-muted-foreground">
            What a project cost beyond the hours. A billable expense goes onto the
            customer&rsquo;s next invoice; once invoiced it is fixed, like time.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="exp-from">From</Label>
            <Input id="exp-from" type="date" className="w-36" value={from}
                   onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="exp-to">To</Label>
            <Input id="exp-to" type="date" className="w-36" value={to}
                   onChange={(e) => setTo(e.target.value)} />
          </div>
          {mayManage && (
            <Button hint={"Manage the expense categories"} variant="outline" onClick={() => setManagingCategories(true)}>
              Categories
            </Button>
          )}
          <Button hint={"Record an expense against a project"} onClick={() => setEditing('new')} disabled={noCategories}>
            <Plus className="mr-1 size-4" /> Record one
          </Button>
        </div>
      </div>

      {/* Said rather than left as a disabled button with no explanation: the old
          system's expense plugin shipped one category called 'Demo' and it was
          not worth migrating, so this list genuinely starts empty. */}
      {noCategories && (
        <Alert>
          <AlertDescription className="text-sm">
            There are no expense categories yet, so nothing can be recorded.
            {mayManage
              ? ' Add one under Categories to get started.'
              : ' Ask HR to add one.'}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">
            {from} to {to}
          </CardTitle>
          {data && (
            <div className="text-sm text-muted-foreground">
              <span className="ts-num">{money(data.totals.costMinor, 'INR')}</span> spent,{' '}
              <span className="ts-num">{money(data.totals.billableChargeMinor, 'INR')}</span> billable
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isPending && <Skeleton className="h-32 w-full" />}
          {data && data.rows.length === 0 && (
            <EmptyState art={ReceiptArt} title="No expense recorded in that period" body="Pick a wider period, or record one with Add expense." />
          )}
          {data && data.rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Person</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Charged</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="ts-num">{row.spentOn}</TableCell>
                    <TableCell>
                      {row.category.name}
                      {row.description && (
                        <span className="block text-xs text-muted-foreground">{row.description}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.project.name}
                      <span className="block text-xs text-muted-foreground">
                        {row.project.customerName}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{row.person.name}</TableCell>
                    <TableCell className="ts-num text-right">
                      {money(row.costMinor, 'INR')}
                      {row.quantity > 1 && (
                        <span className="block text-xs text-muted-foreground">
                          x {row.quantity}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="ts-num text-right">
                      {row.billable ? money(row.chargeMinor, 'INR') : '—'}
                      {row.invoice && (
                        <Badge variant="secondary" className="ml-2">{row.invoice.number}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {/* An invoiced expense offers no edit or delete at all,
                          rather than a button that fails: the refusal is real and
                          a disabled control that explains itself is kinder than
                          an error after the click. */}
                      {row.invoice || row.exported ? (
                        <span className="text-xs text-muted-foreground">invoiced</span>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <Button hint={"Edit this expense"} size="sm" variant="ghost" onClick={() => setEditing(row)}>
                            Edit
                          </Button>
                          <Button hint={"Delete this expense"}
                            size="sm"
                            variant="ghost"
                            disabled={remove.isPending}
                            onClick={() => {
                              setError(null);
                              if (window.confirm(`Delete this ${row.category.name} expense?`)) {
                                remove.mutate(row.id, {
                                  onError: (e) => {
                                    setError(e instanceof ApiError ? e.message : 'Could not delete it.');
                                    toastFailure(`Could not delete the ${row.category.name} expense`)(e);
                                  },
                                });
                              }
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {editing && (
        <ExpenseDialog
          value={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {managingCategories && (
        <CategoryDialog onClose={() => setManagingCategories(false)} />
      )}
    </div>
  );
}

/** A money amount as the form takes it: whole rupees, or rupees and up to two paise digits. */
const AMOUNT = /^\d+(\.\d{1,2})?$/;

function ExpenseDialog({ value, onClose }: { value: ExpenseRow | null; onClose: () => void }) {
  const { data: projects } = useProjects();
  const { data: categories } = useExpenseCategories();
  const save = useSaveExpense();
  const [error, setError] = React.useState<string | null>(null);

  const [projectId, setProjectId] = React.useState(value?.project.id ?? '');
  const [categoryId, setCategoryId] = React.useState(value?.category.id ?? '');
  const [spentOn, setSpentOn] = React.useState(value?.spentOn ?? today());
  const [cost, setCost] = React.useState(value ? toDecimal(value.costMinor) : '');
  const [charge, setCharge] = React.useState(value ? toDecimal(value.chargeMinor) : '');
  const [quantity, setQuantity] = React.useState(String(value?.quantity ?? 1));
  const [description, setDescription] = React.useState(value?.description ?? '');
  const [billable, setBillable] = React.useState(value?.billable ?? true);
  // The field the last submit refused. Each is marked only while its check still fails,
  // so the red border goes the moment the field is fixed.
  const [refused, setRefused] = React.useState<'project' | 'category' | 'cost' | 'charge' | null>(null);
  const refuse = (field: NonNullable<typeof refused>, message: string) => {
    setRefused(field);
    setError(message);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{value ? 'Change this expense' : 'Record an expense'}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            // Checked here so the message names the field; the server's refusal of
            // an empty id would only say a field was not accepted.
            if (!projectId) return refuse('project', 'Choose the project this expense is for.');
            if (!categoryId) return refuse('category', 'Choose a category.');
            if (!AMOUNT.test(cost.trim() || '0')) return refuse('cost', 'Cost must be an amount like 1200 or 1200.50.');
            if (billable && !AMOUNT.test(charge.trim() || '0'))
              return refuse('charge', 'Charge must be an amount like 1200 or 1200.50.');
            setRefused(null);
            save.mutate(
              {
                id: value?.id,
                projectId,
                categoryId,
                spentOn,
                cost: cost.trim() || '0',
                // An unbillable expense charges nothing; sending what is in the
                // box would be refused, and rightly.
                charge: billable ? charge.trim() || '0' : '0',
                quantity: Number(quantity) || 1,
                description: description.trim() || null,
                billable,
              },
              {
                onSuccess: onClose,
                onError: (err) =>
                  setError(err instanceof ApiError ? err.message : 'Could not save that.'),
              },
            );
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Combobox
                aria-invalid={refused === 'project' && !projectId}
                value={projectId}
                onChange={setProjectId}
                options={(projects ?? []).map((p) => ({
                  value: p.id,
                  label: p.name,
                  description: p.customer.name,
                }))}
                placeholder="Pick a project"
                searchPlaceholder="Search projects"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Combobox
                aria-invalid={refused === 'category' && !categoryId}
                value={categoryId}
                onChange={setCategoryId}
                options={(categories ?? []).map((c) => ({
                  value: c.id,
                  label: c.name,
                  description: undefined,
                }))}
                placeholder="Pick a category"
                searchPlaceholder="Search categories"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="exp-date">Date</Label>
              <Input id="exp-date" type="date" value={spentOn}
                     onChange={(e) => setSpentOn(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="exp-qty">Quantity</Label>
              <Input id="exp-qty" type="number" min={1} value={quantity}
                     onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="exp-cost">Cost (INR)</Label>
              <Input id="exp-cost" value={cost} placeholder="1200.00" aria-invalid={refused === 'cost' && !AMOUNT.test(cost.trim() || '0')}
                     onChange={(e) => setCost(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="exp-charge">Charged to the customer</Label>
              <Input id="exp-charge" value={charge} placeholder="1200.00" disabled={!billable} aria-invalid={refused === 'charge' && !AMOUNT.test(charge.trim() || '0')}
                     onChange={(e) => setCharge(e.target.value)} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={billable} onCheckedChange={(v) => setBillable(v === true)} />
            Rebill this to the customer
          </label>

          <div className="space-y-1.5">
            <Label htmlFor="exp-desc">What it was</Label>
            <Textarea id="exp-desc" rows={2} value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Appears on the invoice line." />
          </div>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ onClose }: { onClose: () => void }) {
  const { data: categories } = useExpenseCategories();
  const save = useSaveExpenseCategory();
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Expense categories</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {categories && categories.length > 0 ? (
            <div className="divide-y rounded-md border">
              {categories.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>{c.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {c._count.expenses} expense{c._count.expenses === 1 ? '' : 's'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">None yet.</p>
          )}

          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              save.mutate(
                { name },
                {
                  onSuccess: () => setName(''),
                  onError: (err) =>
                    setError(err instanceof ApiError ? err.message : 'Could not add that.'),
                },
              );
            }}
          >
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Travel, Software, Hardware…" />
            <Button type="submit" disabled={save.isPending || !name.trim()}>Add</Button>
          </form>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toDecimal(minor: string): string {
  const digits = minor.padStart(3, '0');
  return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

function today(): string {
  return orgToday();
}

function monthStart(): string {
  return startOfMonth(orgToday());
}

function monthEnd(): string {
  return endOfMonth(orgToday());
}
